import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { t } from "@/lib/translations";
import { resolveImg } from "@/lib/api";

const links = [
  { id: "menu", key: "nav_menu" },
  { id: "story", key: "nav_story" },
  { id: "gallery", key: "nav_gallery" },
  { id: "videos", key: "nav_videos" },
  { id: "reviews", key: "nav_reviews" },
  { id: "visit", key: "nav_visit" },
];

export const Header = ({ content }) => {
  const { lang, setLang } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    setOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "backdrop-blur-xl bg-[#0A0A0A]/80 border-b border-white/10" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8 h-20 flex items-center justify-between">
        <button data-testid="logo-home" onClick={() => scrollTo("hero")} className="flex items-center gap-3">
          {content.logo ? (
            <img src={resolveImg(content.logo)} alt={content.business_name} className="h-12 md:h-14 w-auto object-contain" />
          ) : (
            <span className="font-display text-lg md:text-xl uppercase tracking-tight leading-none">
              {content.business_name}
            </span>
          )}
        </button>

        <nav className="hidden md:flex items-center gap-9">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-${l.id}`}
              onClick={() => scrollTo(l.id)}
              className="text-sm font-semibold uppercase tracking-wide text-neutral-300 hover:text-[#FF5A00] transition-colors"
            >
              {t(l.key, lang)}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <div className="flex items-center rounded-full border border-white/15 overflow-hidden text-xs font-bold">
            {["es", "en"].map((lng) => (
              <button
                key={lng}
                data-testid={`lang-${lng}`}
                onClick={() => setLang(lng)}
                className={`px-3 py-1.5 uppercase transition-colors ${
                  lang === lng ? "bg-[#FF5A00] text-black" : "text-neutral-400 hover:text-white"
                }`}
              >
                {lng}
              </button>
            ))}
          </div>
          <button
            data-testid="header-order-btn"
            onClick={() => scrollTo("visit")}
            className="hidden sm:inline-flex items-center rounded-full bg-[#FF5A00] hover:bg-[#FF7A33] text-black font-bold uppercase tracking-wide text-sm px-5 py-2.5 transition-all hover:scale-105"
          >
            {t("order", lang)}
          </button>
          <button data-testid="mobile-menu-toggle" className="md:hidden text-white" onClick={() => setOpen(!open)}>
            <div className="space-y-1.5">
              <span className="block w-6 h-0.5 bg-white" />
              <span className="block w-6 h-0.5 bg-white" />
            </div>
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden border-t border-white/10 bg-[#0A0A0A]/95 backdrop-blur-xl px-5 py-6 flex flex-col gap-4">
          {links.map((l) => (
            <button key={l.id} data-testid={`nav-mobile-${l.id}`} onClick={() => scrollTo(l.id)}
              className="text-left text-base font-semibold uppercase tracking-wide text-neutral-200">
              {t(l.key, lang)}
            </button>
          ))}
        </div>
      )}
    </header>
  );
};
